import type { RegionCode } from "brandRegionConfig";
import { getBrand } from "core/utils/getBrand";
import { isValidRegion } from "core/utils/isValidRegion";
import { useRouter } from "next/router";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { AvailableRoutes } from "resources/availableRoutes";
import { useAvailableRegions } from "../hooks/useAvailableRegions";

export const REGION_KEY = "region";

export const OverrideRegionContext = createContext<RegionCode | null>(null);
export const RegionContext = createContext<RegionCode>("ca");
RegionContext.displayName = "RegionContext";

export function RegionContextProvider({ children }: { children: ReactNode }) {
  const overrideRegion = useContext(OverrideRegionContext);
  const {
    query: { region: queryRegion, ...query },
    replace,
    pathname,
  } = useRouter();
  const availableRegions = useAvailableRegions();
  const { brandCode } = getBrand();
  if (!brandCode) {
    throw new Error("useRegion hook requires a brand code");
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omitting query from dependencies
  const region = useMemo(() => {
    if (overrideRegion) {
      return overrideRegion;
    }
    const isQueryValid = isValidRegion(queryRegion, availableRegions);
    if (isQueryValid) {
      return queryRegion;
    }

    const SignInPath: AvailableRoutes = "/signin-oidc";
    const isSignInPath = pathname === SignInPath;
    const shouldReplaceRegion = !isSignInPath && pathname.includes("region");

    const storageKey = `user-storage.${brandCode}-${REGION_KEY}`;
    const storageRegion = JSON.parse(localStorage.getItem(storageKey) ?? '""');
    const isStorageRegionValid = isValidRegion(storageRegion, availableRegions);
    if (isStorageRegionValid) {
      if (shouldReplaceRegion) {
        replace({
          pathname,
          query: { ...query, region: storageRegion },
        });
      }
      return storageRegion;
    }

    if (availableRegions.includes("ca")) {
      if (shouldReplaceRegion) {
        replace({
          pathname,
          query: { ...query, region: "ca" },
        });
      }
      return "ca";
    }
    const fallbackRegion = availableRegions[0];
    if (fallbackRegion) {
      if (shouldReplaceRegion) {
        replace({
          pathname,
          query: { ...query, region: fallbackRegion },
        });
      }
      return fallbackRegion;
    }
    return null;
  }, [
    overrideRegion,
    queryRegion,
    availableRegions,
    pathname,
    brandCode,
    replace,
  ]);

  if (!region) {
    throw new Error(
      `no "availableRegions" configured for the brand ${brandCode}`
    );
  }
  return (
    <RegionContext.Provider value={region}>{children}</RegionContext.Provider>
  );
}
